'use client';

import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useIsClient } from 'usehooks-ts';
import IconLinkDisable from '@/components/assets/icon_link_disable.svg';
import ViverseButton from '@/components/ViverseButton';
import { useAuthContext } from '@/context/AuthContext';
const REDIRECT_DELAY_MS = 3000;

/**
 * Renders a bottom-center toast + triggers a 3-second redirect to home when a
 * visitor lands on a suspended account's profile page.
 *
 * When the *current* user is the suspended account (isSuspended === true),
 * the global SuspensionModal already handles the blocking — no redirect needed.
 */
export default function SuspendedAccountRedirect() {
  const $ = _c(30);
  const {
    isSuspended: currentUserIsSuspended
  } = useAuthContext();
  const router = useRouter();
  const t = useTranslations();
  const isClient = useIsClient();
  let t0;
  let t1;
  if ($[0] !== currentUserIsSuspended || $[1] !== router) {
    t0 = () => {
      if (currentUserIsSuspended) {
        return;
      }
      const timer = setTimeout(() => {
        router.push("/");
      }, REDIRECT_DELAY_MS);
      return () => clearTimeout(timer);
    };
    t1 = [currentUserIsSuspended, router];
    $[0] = currentUserIsSuspended;
    $[1] = router;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  if (currentUserIsSuspended || !isClient) {
    return null;
  }
  let t2;
  if ($[4] !== router) {
    t2 = () => {
      router.push("/");
    };
    $[4] = router;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleExplore = t2;
  let t3;
  let t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      y: 100,
      opacity: 0
    };
    t4 = {
      y: 0,
      opacity: 1
    };
    t5 = {
      duration: 0.4,
      ease: "easeOut"
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <motion.div initial={{
      width: 0,
      display: "none"
    }} animate={{
      width: "120px",
      display: "block"
    }} transition={{
      duration: 0.5
    }} className="absolute top-0 left-0 h-full bg-[linear-gradient(90deg,#F8E64A_0%,rgba(248,230,74,0)_100%)] opacity-50" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IconLinkDisable className="z-interactive size-[30px] shrink-0" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t) {
    t8 = t("account.suspension.profile.toast.title");
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8) {
    t9 = <h2 className="subheader-2 text-[18px] leading-8 tracking-[0.2px]">{t8}</h2>;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t) {
    t10 = t("account.suspension.profile.toast.message");
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10) {
    t11 = <p className="body-1">{t10}</p>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t9) {
    t12 = <div className="flex flex-col">{t9}{t11}</div>;
    $[19] = t11;
    $[20] = t9;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t) {
    t13 = t("account.suspension.profile.toast.explore");
    $[22] = t;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== handleExplore || $[25] !== t13) {
    t14 = <ViverseButton size="small" className="shrink-0" onClick={handleExplore}>{t13}</ViverseButton>;
    $[24] = handleExplore;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t12 || $[28] !== t14) {
    t15 = <div className="min-h-screen"><motion.div initial={t3} animate={t4} transition={t5} className="bg-vive-primary-80 fixed bottom-4 left-1/2 z-999999999 -translate-x-1/2 overflow-hidden rounded-lg px-6 py-3 md:min-w-[400px]">{t6}<div className="flex items-center gap-4">{t7}<div className="z-interactive flex w-full items-center justify-between gap-4">{t12}{t14}</div></div></motion.div></div>;
    $[27] = t12;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
}